import { z } from 'zod';
import { defineFeature } from '../core/defineFeature.js';
import { FeatureType } from '../core/types.js';
const FormWorkflowsCategory = { id: 8, name: 'Form Workflows' };
/**
 * Json feature whose runtime value is a structured array. Codec is
 * 'json-string' so the value column stores a JSON-encoded array; parsing
 * happens at the boundary. Replaces the JsonFeatureSchemaRegistry
 * { form_workflows: ... } indirection in the legacy types/features.ts.
 */
const FormWorkflowItemSchema = z.object({ workflowIdentifier: z.uuid() });
const FormWorkflowsSchema = z.array(FormWorkflowItemSchema);
export const formWorkflows = defineFeature({
    type: FeatureType.Json,
    propName: 'form_workflows',
    uiName: 'formWorkflows',
    displayName: 'Form Workflows',
    description: 'Form workflows that can be triggered by the chat',
    category: FormWorkflowsCategory,
    areas: ['agent'],
    codec: 'json-string',
    schema: FormWorkflowsSchema,
    default: [],
    isSystem: true,
    editor: 'form-workflows',
    surfacedIn: ['agent'],
});
/**
 * Master Boolean toggle that enables the form workflow capability. The
 * value is defined at the agent scope and surfaced for override in the
 * application, brand, account, and module management tabs. The structured
 * payload lives in [[form_workflows]]; this flag answers "should we
 * surface form-workflow UI at all?". Created by migration
 * 20251007000000_form_workflow_features.
 */
export const formWorkflow = defineFeature({
    type: FeatureType.Boolean,
    propName: 'form_workflow',
    uiName: 'formWorkflow',
    displayName: 'Form Workflow',
    description: 'Enable form workflow capability; specifics stored in registry',
    category: FormWorkflowsCategory,
    areas: ['agent'],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
    surfacedIn: ['agent'],
});
/**
 * Gives the agent the form tools that read and fill a form in the conversation (describe, state, update) in place of the
 * single fire-and-forget tool. Off by default; the legacy tool stays available until every agent has moved.
 */
export const agentForms = defineFeature({
    type: FeatureType.Boolean,
    propName: 'agent_forms',
    uiName: 'agentForms',
    displayName: 'Agent Forms',
    description: 'Let the agent read, fill and check a form in the conversation',
    category: FormWorkflowsCategory,
    areas: ['agent'],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
    surfacedIn: ['agent'],
});
export const formWorkflowsFeatures = [formWorkflows, formWorkflow, agentForms];
